import MessageAttachmentsItem from "@/js/Mailboxes/Entities/Message/MessageAttachmentsItem";
import ComposeItem            from "../Entities/ComposeItem";
import LanguageSingleton      from "@/js/Shared/Language/Language";

export {ComposeHTTPMappingSingleton as default};

/**
* Compose HTTP Mapping
*
* Maps compose data into compose entity objects
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ComposeHTTPMapping {
  /**
   * Maps the server compose task
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ComposeItem} compose compose item
   * @param {Object}      results server results data
   *
   * @return {void}
   */
  mapCompose(compose, results) {
    compose.hmac = results.hmac;
    compose.id   = results.cacheid || compose.id;
  }

  /**
   * Maps Horde Compose Body data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ComposeItem} compose compose item
   * @param {Object}      results server results data
   *
   * @return {void}
   */
  mapComposeData(compose, results) {
    compose.subject = results.subject;
    compose.message = results.body;

    compose.setIdentity(results.identity);

    if (results.addr) {
      results.addr.to.forEach(recipient => {
        compose.to.addPersonByEmail(recipient.e || recipient.v);
      });

      results.addr.cc.forEach(recipient => {
        compose.cc.addPersonByEmail(recipient.e || recipient.v);
      });

      results.addr.bcc.forEach(recipient => {
        compose.bcc.addPersonByEmail(recipient.e || recipient.v);
      });
    }
  }

  /**
   * Maps Horde File Attachments task
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ComposeItem} compose compose item
   * @param {Object}      results server results data
   *
   * @return {void}
   */
  mapComposeAttachment(compose, results) {
    results.forEach(item => {
      let attachment             = new MessageAttachmentsItem();

      attachment.id          = item.num;
      attachment.name        = item.name;
      attachment.preview_url = item.url;
      attachment.size        = item.rawSize;
      attachment.type        = item.icon;
      attachment.target      = 'internal';

      compose.attachments_size += attachment.size;
      compose.attachments_total++;
      compose.attachments.add(attachment, attachment.id);
    });
  }

  /**
   * Maps the server delete attachment task
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ComposeItem} compose compose item
   * @param {Object}      results server results data
   *
   * @return {void}
   */
  mapDeleteAttachment(compose, results) {
    results.ids.forEach(id => {
      compose.attachments.delete(id);
      compose.attachments_total--;
    });
  }

  /**
   * Maps the server compose add attachment task
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ComposeItem} compose compose item
   * @param {Object}      results server results data
   *
   * @return {void}
   */
  mapAddAttachment(compose, results) {
    if (!results.success || !results.img) {
      return;
    }

    let attachment             = new MessageAttachmentsItem();
    let upload                 = compose.attachments_upload.get(results.file_id);
    attachment.preview_url     = results.img.src;
    attachment.related         = results.img.related;
    attachment.related_id      = results.file_id;
    attachment.target          = 'internal';
    compose.attachments_size  += upload.file.size;
    compose.attachments_total++;
    compose.attachments_inline.add(attachment, results.file_id);
  }

  /**
   * Maps Horde File Attachments task
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ComposeItem} compose       compose item
   * @param {FilesEvent}  files         files data
   * @param {Object}      transfer_data server transfer data
   * @param {Object}      sign_data     server sign data
   *
   * @return {MessageAttachmentsItem}
   */
  mapComposeTransferAttachment(compose, files, results, sign_data) {
    let attachment = new MessageAttachmentsItem();

    attachment.id          = results.id;
    attachment.preview_url = sign_data.download_link;
    attachment.target      = 'sapo-transfer';
    attachment.size        = files.size;
    attachment.files_count = files.items.length;
    attachment.name        = (files.items.length === 1)
                           ? files.items[0].name + ' ' + LanguageSingleton.say('composer.modals.transfer_file_via')
                           : LanguageSingleton.say('composer.modals.transfer_files_name', [files.items.length]);

    compose.attachments_total++;
    compose.attachments.add(attachment, attachment.id);
    return attachment;
  }
}

/**
 * @type {ComposeHTTPMapping}
 */
const ComposeHTTPMappingSingleton = new ComposeHTTPMapping();